import { useEffect, useRef, useState } from 'react';

// Global flag to prevent multiple audio contexts
let globalAudioContext = null;
let globalIsInitialized = false;
let globalMusicStarted = false;
let globalTracksLoaded = false;
let globalTracks = {};
let globalGainNodes = {};
let globalIdleTrack = null;
let globalIdleGain = null;

// Track if we need to load files after context unlock
let globalNeedsFileLoad = false;

// Track the current MusicManager instance's initialization function
let globalInitializeCallback = null;

// Reset function to clear all global state
export const resetGlobalAudioState = () => {
  console.log('🎵 Resetting global audio state');
  globalAudioContext = null;
  globalIsInitialized = false;
  globalMusicStarted = false;
  globalTracksLoaded = false;
  globalTracks = {};
  globalGainNodes = {};
  globalIdleTrack = null;
  globalIdleGain = null;
  globalNeedsFileLoad = false;
};

// Export a global initialization function
export const initializeGlobalAudio = () => {
  console.log('🎵 Global audio initialization requested');
  console.log('🎵 Current state - initialized:', globalIsInitialized, 'context:', !!globalAudioContext, 'tracks loaded:', globalTracksLoaded);
  
  // Check if context exists but is closed (mobile browser was closed)
  if (globalAudioContext && globalAudioContext.state === 'closed') {
    console.log('🎵 Audio context is closed, resetting...');
    resetGlobalAudioState();
  }
  
  if (globalIsInitialized && globalTracksLoaded) {
    console.log('🎵 Audio already initialized with tracks loaded');
    return Promise.resolve();
  }
  
  // For iOS, we need to create and unlock the AudioContext synchronously
  // during the user interaction, BEFORE any async operations
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!globalAudioContext) {
      globalAudioContext = new AudioContext();
      console.log('🎵 Created AudioContext in user gesture, state:', globalAudioContext.state);
      
      // Immediately resume if suspended (synchronously in user gesture)
      if (globalAudioContext.state === 'suspended') {
        // Play a silent sound immediately to unlock on iOS
        const buffer = globalAudioContext.createBuffer(1, 1, 22050);
        const source = globalAudioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(globalAudioContext.destination);
        source.start(0);
        
        // Resume the context (this is synchronous on most browsers)
        globalAudioContext.resume();
        console.log('🎵 Unlocked audio context in user gesture');
      }
      
      globalIsInitialized = true;
      globalNeedsFileLoad = true; // Mark that we need to load files
      console.log('🎵 Context unlocked, files will be loaded by MusicManager');
    } else if (globalAudioContext.state === 'suspended') {
      // Context exists but is suspended - try to resume it
      console.log('🎵 Existing context is suspended, resuming...');
      globalAudioContext.resume();
      globalIsInitialized = true;
      globalNeedsFileLoad = true;
    } else {
      console.log('🎵 Context already exists and running');
      globalIsInitialized = true;
      globalNeedsFileLoad = true;
    }
    
    // If we have a callback from the MusicManager instance, call it to load tracks
    if (globalInitializeCallback && !globalTracksLoaded) {
      console.log('🎵 Triggering track loading from global initialization');
      globalInitializeCallback();
    }
  } catch (error) {
    console.error('🎵 Failed to unlock audio in user gesture:', error);
  }
  
  return Promise.resolve();
};

const MusicManager = ({ combo, soundEnabled, musicVolume = 1.0, onAudioFailure }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentLayers, setCurrentLayers] = useState(0);
  const [isIdle, setIsIdle] = useState(true);
  
  const audioContextRef = useRef(null);
  const tracksRef = useRef({});
  const gainNodesRef = useRef({});
  const masterGainRef = useRef(null);
  const idleTrackRef = useRef(null);
  const idleGainRef = useRef(null);
  
  const layers = [
    { name: 'drums', threshold: 2, file: './sounds/music/mp3/GoodDaySoFar/drums.mp3' },
    { name: 'bass', threshold: 100, file: './sounds/music/mp3/GoodDaySoFar/bass.mp3' },
    { name: 'melody', threshold: 200, file: './sounds/music/mp3/GoodDaySoFar/melody.mp3' }
  ];

  // Function needs to be defined before useEffect that uses it
  const loadTrackWithRetry = async (url, name, retries = 0) => {
    try {
      console.log(`🎵 Loading ${name} from ${url} (attempt ${retries + 1})...`);
      
      // Add timeout to fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      console.log(`🎵 ${name} response received, downloading...`);
      const arrayBuffer = await response.arrayBuffer();
      console.log(`🎵 ${name} downloaded: ${(arrayBuffer.byteLength / 1024).toFixed(1)} KB`);
      
      // Check if audioContextRef.current exists
      if (!audioContextRef.current) {
        console.error(`🎵 No audio context available for decoding ${name}!`);
        throw new Error('AudioContext not available');
      }
      
      console.log(`🎵 Starting decode for ${name}...`);
      console.log(`🎵 AudioContext state before decode:`, audioContextRef.current.state);
      
      try {
        const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
        console.log(`🎵 ${name} decoded successfully`);
        console.log(`🎵 Audio buffer details - duration: ${audioBuffer.duration}s, channels: ${audioBuffer.numberOfChannels}, sampleRate: ${audioBuffer.sampleRate}`);
        return audioBuffer;
      } catch (decodeError) {
        console.error(`🎵 Failed to decode ${name}:`, decodeError);
        throw decodeError;
      }
    } catch (error) {
      console.error(`🎵 Failed to load ${name}:`, error.message);
      console.error(`🎵 Error details:`, error);
      
      if (retries < 2) {
        console.log(`🎵 Retrying ${name} in 1 second...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return loadTrackWithRetry(url, name, retries + 1);
      }
      
      throw error;
    }
  };

  const loadIdleTrack = async () => {
    try {
      const audioBuffer = await loadTrackWithRetry('./sounds/music/mp3/idle.mp3', 'idle');
        
        idleGainRef.current = audioContextRef.current.createGain();
        idleGainRef.current.gain.value = 0.5; // 50% of music volume
        console.log('🎵 Idle gain created with value:', idleGainRef.current.gain.value);
        idleGainRef.current.connect(masterGainRef.current);
        
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.loop = true;
        source.connect(idleGainRef.current);
        
        idleTrackRef.current = {
          buffer: audioBuffer,
          source: source
        };
        
        // Save to global
        globalIdleTrack = idleTrackRef.current;
        globalIdleGain = idleGainRef.current;
        console.log('🎵 Idle track loaded successfully');
    } catch (error) {
      console.error('🎵 Failed to load idle music:', error);
      console.error('🎵 Load error details:', {
        message: error.message,
        stack: error.stack
      });
    }
  };

  const loadTrack = async (layer) => {
    console.log('🎵 Loading track:', layer.name, layer.file);
    try {
      const audioBuffer = await loadTrackWithRetry(layer.file, layer.name);
      
      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = 0;
      gainNode.connect(masterGainRef.current);
      
      gainNodesRef.current[layer.name] = gainNode;
      
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.loop = true;
      source.connect(gainNode);
      
      tracksRef.current[layer.name] = {
        buffer: audioBuffer,
        source: source,
        layer: layer
      };
      
      // Save to global
      globalTracks[layer.name] = tracksRef.current[layer.name];
      globalGainNodes[layer.name] = gainNode;
    } catch (error) {
      console.error(`🎵 ERROR: Failed to load ${layer.name}:`, error);
      console.error(`🎵 Error details:`, error.message);
      
      // On mobile, files might be too large - notify about failure
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        console.log('🎵 Mobile device detected - music file may be too large');
        if (onAudioFailure && onAudioFailure.current) {
          onAudioFailure.current();
        }
      }
    }
  };

  const startMusic = () => {
    if (globalMusicStarted) {
      console.log('🎵 Music already started, checking if sources are valid...');
      
      // For PWA, sources might be invalid even if globalMusicStarted is true
      if (!checkSourcesValid()) {
        console.log('🎵 Sources are invalid, need to recreate them');
        if (!recreateSources()) {
          console.log('🎵 Failed to recreate sources, cannot start music');
          return;
        }
      }
      return;
    }
    
    // Check global state instead of local state since we just set it
    if (!globalTracksLoaded) {
      console.log('🎵 Cannot start music - tracks not loaded yet');
      return;
    }
    
    console.log('🎵 Starting music...');
    const currentTime = audioContextRef.current.currentTime;
    
    // Start idle music first
    if (idleTrackRef.current && idleTrackRef.current.source) {
      try {
        idleTrackRef.current.source.start(currentTime);
        console.log('🎵 Idle music started');
      } catch (error) {
        if (error.name === 'InvalidStateError') {
          console.log('🎵 Idle music already started');
        } else {
          console.error('🎵 Failed to start idle music:', error);
        }
      }
    }
    
    // Start all gameplay tracks at the same time
    Object.keys(tracksRef.current).forEach(trackName => {
      const track = tracksRef.current[trackName];
      if (track && track.source) {
        try {
          track.source.start(currentTime);
          console.log(`🎵 ${trackName} started`);
        } catch (error) {
          if (error.name === 'InvalidStateError') {
            console.log(`🎵 ${trackName} already started`);
          } else {
            console.error(`🎵 Failed to start ${trackName}:`, error);
          }
        }
      }
    });
    
    globalMusicStarted = true;
    console.log('🎵 All music started');
  };

  const stopMusic = () => {
    console.log('🎵 Stopping all music...');
    
    // Stop idle music
    if (idleTrackRef.current && idleTrackRef.current.source) {
      try {
        idleTrackRef.current.source.stop();
        console.log('🎵 Idle music stopped');
      } catch (error) {
        console.error('🎵 Failed to stop idle music:', error);
      }
    }
    
    // Stop all gameplay tracks
    Object.keys(tracksRef.current).forEach(trackName => {
      const track = tracksRef.current[trackName];
      if (track && track.source) {
        try {
          track.source.stop();
          console.log(`🎵 ${trackName} stopped`);
        } catch (error) {
          console.error(`🎵 Failed to stop ${trackName}:`, error);
        }
      }
    });
    
    globalMusicStarted = false;
    console.log('🎵 All music stopped');
  };

  const initializeAudio = async () => {
    console.log('🎵 initializeAudio called');
    
    // Prevent duplicate initialization
    if (window.musicInitializing) {
      console.log('🎵 Already initializing, aborting this call');
      return;
    }
    
    if (globalTracksLoaded && isLoaded) {
      console.log('🎵 Tracks already loaded and component is loaded');
      return;
    }
    
    window.musicInitializing = true;
    
    console.log('🎵 soundEnabled:', soundEnabled, 'musicVolume:', musicVolume);
    console.log('🎵 globalIsInitialized:', globalIsInitialized);
    console.log('🎵 User Agent:', navigator.userAgent);
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    console.log('🎵 Is Mobile:', isMobile, 'Is iOS:', isIOS);
    console.log('🎵 Using compressed MP3 files (~800KB each instead of 9MB WAV files)');
    
    // Check if we have a global context from early initialization
    if (globalAudioContext) {
      console.log('🎵 Using pre-initialized global AudioContext, state:', globalAudioContext.state);
      audioContextRef.current = globalAudioContext;
      
      // Check if tracks are already loaded
      if (globalTracksLoaded) {
        console.log('🎵 Tracks already loaded, reconnecting...');
        masterGainRef.current = globalAudioContext.createGain();
        masterGainRef.current.gain.value = soundEnabled ? (musicVolume * 0.75) : 0;
        masterGainRef.current.connect(globalAudioContext.destination);
        
        // Reconnect global tracks
        if (globalIdleGain) {
          globalIdleGain.disconnect();
          globalIdleGain.connect(masterGainRef.current);
          idleGainRef.current = globalIdleGain;
          idleTrackRef.current = globalIdleTrack;
        }
        
        Object.keys(globalGainNodes).forEach(key => {
          globalGainNodes[key].disconnect();
          globalGainNodes[key].connect(masterGainRef.current);
        });
        
        gainNodesRef.current = globalGainNodes;
        tracksRef.current = globalTracks;
        setIsLoaded(true);
        window.musicInitializing = false;
        return;
      }
    } else {
      // Fallback: create context here if not already created
      console.log('🎵 No global context found, creating new one...');
      
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        console.log('🎵 AudioContext available:', !!AudioContext);
        
        // Reset global state when creating new context
        globalIsInitialized = false;
        globalTracksLoaded = false;
        globalNeedsFileLoad = false;
        
        // Create new context
        globalAudioContext = new AudioContext();
        console.log('🎵 Created new AudioContext, state:', globalAudioContext.state);
        audioContextRef.current = globalAudioContext;
      } catch (error) {
        console.error('🎵 Failed to create AudioContext:', error);
        if (onAudioFailure && onAudioFailure.current) {
          onAudioFailure.current();
        }
        return;
      }
    }
    
    // Check if context needs to be resumed (should auto-resume due to user interaction)
    console.log('🎵 Initial audio context state after creation:', audioContextRef.current.state);
    if (audioContextRef.current.state === 'suspended') {
      console.log('🎵 Context is suspended, trying to resume...');
      try {
        // First try to resume the context
        await audioContextRef.current.resume();
        console.log('🎵 Context resume() called, new state:', audioContextRef.current.state);
        
        // If still suspended, try the silent buffer trick
        if (audioContextRef.current.state === 'suspended') {
          console.log('🎵 Still suspended after resume(), trying silent buffer...');
          const silentBuffer = audioContextRef.current.createBuffer(1, 1, 22050);
          const silentSource = audioContextRef.current.createBufferSource();
          silentSource.buffer = silentBuffer;
          silentSource.connect(audioContextRef.current.destination);
          silentSource.start(0);
          console.log('🎵 Played silent buffer to unlock audio');
          
          // Try resume again after silent buffer
          await audioContextRef.current.resume();
          console.log('🎵 Context state after silent buffer and resume:', audioContextRef.current.state);
        }
        
        if (audioContextRef.current.state === 'running') {
          console.log('🎵 ✅ Context successfully running!');
        } else {
          console.log('🎵 ⚠️ Context still not running, state:', audioContextRef.current.state);
          // Notify about audio failure - likely silent mode or audio restrictions
          if (onAudioFailure && onAudioFailure.current) {
            console.log('🎵 Notifying about audio failure due to context resume failure');
            onAudioFailure.current();
          }
        }
      } catch (error) {
        console.error('🎵 Failed to resume context:', error);
        console.log('🎵 Continuing with suspended context - may need user interaction');
        // Notify about audio failure - likely silent mode or audio restrictions
        if (onAudioFailure && onAudioFailure.current) {
          console.log('🎵 Notifying about audio failure due to context resume failure');
          onAudioFailure.current();
        }
      }
    } else {
      console.log('🎵 Context ready, state:', audioContextRef.current.state);
    }
    
    try {
      masterGainRef.current = audioContextRef.current.createGain();
      masterGainRef.current.gain.value = soundEnabled ? (musicVolume * 0.75) : 0;  // 75% of slider value
      console.log('🎵 Master gain created with value:', masterGainRef.current.gain.value);
      masterGainRef.current.connect(audioContextRef.current.destination);

      // Load idle music with error handling
      console.log('🎵 Loading idle track...');
      try {
        await loadIdleTrack();
      } catch (error) {
        console.error('🎵 Failed to load idle track, continuing without it:', error);
      }
      
      // Load gameplay tracks with error handling
      console.log('🎵 Loading gameplay tracks...');
      const loadPromises = layers.map(layer => loadTrack(layer).catch(error => {
        console.error(`🎵 Failed to load ${layer.name}, continuing without it:`, error);
        return null;
      }));
      
      await Promise.all(loadPromises);
      
      // Check if at least some tracks loaded
      const loadedTracks = Object.keys(tracksRef.current).length;
      console.log(`🎵 Loaded ${loadedTracks}/${layers.length} gameplay tracks`);
      console.log('🎵 Tracks loaded:', Object.keys(tracksRef.current));
      console.log('🎵 Idle track loaded:', !!idleTrackRef.current);
      
      if (loadedTracks === 0 && !idleTrackRef.current) {
        console.error('🎵 No tracks loaded successfully');
        if (onAudioFailure && onAudioFailure.current) {
          onAudioFailure.current();
        }
        return;
      }
      
      setIsLoaded(true);
      globalIsInitialized = true;
      globalTracksLoaded = true;
      console.log('🎵 Audio initialized successfully');
      console.log('🎵 About to call startMusic()');
      startMusic();
      window.musicInitializing = false;
    } catch (error) {
      console.error('🎵 Failed to initialize audio:', error);
      window.musicInitializing = false;
      // Notify about audio failure
      if (onAudioFailure && onAudioFailure.current) {
        console.log('🎵 Notifying about audio failure due to initialization error');
        onAudioFailure.current();
      }
    }
  };

  useEffect(() => {
    // Register the initialization callback
    globalInitializeCallback = initializeAudio;
    
    // Prevent double initialization - use a flag to track if we're already initializing
    if (window.musicInitializing) {
      console.log('🎵 Already initializing, skipping...');
      return;
    }
    
    // Check if context was unlocked and needs file loading
    if (globalNeedsFileLoad && !globalTracksLoaded && !isLoaded) {
      console.log('🎵 Context was unlocked, loading audio files now...');
      globalNeedsFileLoad = false; // Reset flag
      window.musicInitializing = true;
      initializeAudio().finally(() => {
        window.musicInitializing = false;
      });
      return;
    }
    
    // Check if context was already unlocked (e.g., from StartScreen)
    if (globalIsInitialized && globalAudioContext && !globalTracksLoaded && !isLoaded) {
      console.log('🎵 Context unlocked but tracks not loaded, loading now...');
      window.musicInitializing = true;
      initializeAudio().finally(() => {
        window.musicInitializing = false;
      });
      return;
    }
    
    // Only initialize if not already done globally
    if (globalIsInitialized && globalTracksLoaded) {
      console.log('🎵 Audio already initialized globally with tracks loaded');
      // Set up local references
      initializeAudio();
      return;
    }
    
    // Only try on user interaction - don't try immediately on mount
    const handleUserInteraction = (event) => {
      console.log('🎵 User interaction detected in MusicManager, initializing audio...');
      console.log('🎵 Event type:', event.type, 'isTrusted:', event.isTrusted);
      
      // Check if audio context is closed (happens after browser close on mobile)
      if (globalAudioContext && globalAudioContext.state === 'closed') {
        console.log('🎵 Audio context is closed, resetting and reinitializing...');
        resetGlobalAudioState();
        setIsLoaded(false);
        audioContextRef.current = null;
        tracksRef.current = {};
        gainNodesRef.current = {};
        idleTrackRef.current = null;
        idleGainRef.current = null;
      }
      
      // Always try to initialize if tracks aren't loaded
      if (!globalTracksLoaded && !isLoaded) {
        console.log('🎵 Tracks not loaded, initializing now...');
        initializeAudio();
      }
      // Remove listeners after initialization attempt
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('touchend', handleUserInteraction);
    };
    
    console.log('🎵 Setting up user interaction listeners for audio initialization');
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    document.addEventListener('touchend', handleUserInteraction);

    return () => {
      globalInitializeCallback = null; // Clear callback on unmount
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('touchend', handleUserInteraction);
      // Don't stop music on unmount - let it persist
    };
  }, []); // Only initialize once on mount

  // Helper functions for checking and recreating sources
  const checkSourcesValid = () => {
    // Check if any sources have ended unexpectedly (happens in PWA)
    if (idleTrackRef.current && idleTrackRef.current.source) {
      try {
        // Try to access a property to see if source is still valid
        const _ = idleTrackRef.current.source.playbackRate.value;
      } catch (e) {
        console.log('🎵 Idle source is invalid, needs recreation');
        return false;
      }
    }
    
    for (const trackName in tracksRef.current) {
      const track = tracksRef.current[trackName];
      if (track && track.source) {
        try {
          // Try to access a property to see if source is still valid
          const _ = track.source.playbackRate.value;
        } catch (e) {
          console.log(`🎵 ${trackName} source is invalid, needs recreation`);
          return false;
        }
      }
    }
    
    return true;
  };

  const recreateSources = async () => {
    console.log('🎵 Recreating audio sources...');
    
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      console.log('🎵 Cannot recreate sources - context invalid');
      return false;
    }
    
    try {
      // Recreate idle source
      if (idleTrackRef.current && idleTrackRef.current.buffer) {
        const newSource = audioContextRef.current.createBufferSource();
        newSource.buffer = idleTrackRef.current.buffer;
        newSource.loop = true;
        newSource.connect(idleGainRef.current);
        idleTrackRef.current.source = newSource;
        globalIdleTrack.source = newSource;
      }
      
      // Recreate gameplay sources
      for (const trackName in tracksRef.current) {
        const track = tracksRef.current[trackName];
        if (track && track.buffer) {
          const newSource = audioContextRef.current.createBufferSource();
          newSource.buffer = track.buffer;
          newSource.loop = true;
          newSource.connect(gainNodesRef.current[trackName]);
          track.source = newSource;
          globalTracks[trackName].source = newSource;
        }
      }
      
      console.log('🎵 Sources recreated successfully');
      return true;
    } catch (error) {
      console.error('🎵 Failed to recreate sources:', error);
      return false;
    }
  };
  
  // Handle visibility changes (when app comes back into focus)
  useEffect(() => {
    // Detect if running as PWA
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone || 
                  document.referrer.includes('android-app://');
    
    console.log('🎵 Running as PWA:', isPWA);
    
    let wasHidden = false;
    let contextWasInterrupted = false;
    
    const handleVisibilityChange = () => {
      console.log('🎵 Visibility changed, document.hidden:', document.hidden);
      
      if (document.hidden) {
        wasHidden = true;
        // Check if music is playing when we lose focus
        if (globalMusicStarted) {
          contextWasInterrupted = true;
          console.log('🎵 App hidden while music was playing');
        }
      } else if (wasHidden) {
        // App is visible again after being hidden
        wasHidden = false;
        
        if (audioContextRef.current) {
          console.log('🎵 App visible, audio context state:', audioContextRef.current.state);
          
          // Check if context is closed (happens on iOS when browser is closed)
          if (audioContextRef.current.state === 'closed') {
            console.log('🎵 Audio context was closed, reinitializing...');
            // Reset everything and wait for next user interaction
            resetGlobalAudioState();
            setIsLoaded(false);
            audioContextRef.current = null;
            tracksRef.current = {};
            gainNodesRef.current = {};
            idleTrackRef.current = null;
            idleGainRef.current = null;
            contextWasInterrupted = false;
            return;
          }
          
          if (audioContextRef.current.state === 'suspended') {
            console.log('🎵 Audio context suspended, attempting to resume...');
            audioContextRef.current.resume().then(() => {
              console.log('🎵 Audio context resumed after visibility change');
              
              // For PWA, check if we need to restart music after resume
              if (isPWA && contextWasInterrupted && !globalMusicStarted) {
                console.log('🎵 PWA: Restarting music after context resume');
                // Need to reinitialize if tracks were lost
                if (!globalTracksLoaded) {
                  console.log('🎵 PWA: Tracks lost, need full reinitialization');
                  initializeAudio();
                } else {
                  startMusic();
                }
                contextWasInterrupted = false;
              }
            }).catch(error => {
              console.error('🎵 Failed to resume audio context:', error);
              // On desktop PWA, context might need reinitialization
              if (isPWA) {
                console.log('🎵 PWA: Context resume failed, resetting for reinitialization');
                resetGlobalAudioState();
                setIsLoaded(false);
                audioContextRef.current = null;
                contextWasInterrupted = false;
              }
            });
          } else if (audioContextRef.current.state === 'running') {
            // Context is running but music might have stopped
            if (contextWasInterrupted && !globalMusicStarted) {
              console.log('🎵 Context running but music stopped, restarting...');
              startMusic();
              contextWasInterrupted = false;
            }
          }
          
          // Check if music needs to be restarted
          if (globalIsInitialized && !globalMusicStarted && globalTracksLoaded) {
            console.log('🎵 Music was stopped, restarting...');
            startMusic();
          }
        } else if (globalIsInitialized && !globalTracksLoaded) {
          // Context was lost but we had initialized before
          console.log('🎵 Audio context lost, needs reinitialization on next interaction');
          resetGlobalAudioState();
          setIsLoaded(false);
          contextWasInterrupted = false;
        }
      }
    };
    
    // Handle focus events - more reliable for desktop PWA
    const handleFocus = () => {
      console.log('🎵 Window focused');
      
      if (audioContextRef.current) {
        console.log('🎵 Focus: Audio context state:', audioContextRef.current.state);
        
        if (audioContextRef.current.state === 'suspended') {
          console.log('🎵 Focus: Resuming suspended context');
          audioContextRef.current.resume().then(() => {
            console.log('🎵 Focus: Context resumed successfully');
            
            // Check if music should be playing
            if (globalTracksLoaded && soundEnabled) {
              console.log('🎵 Focus: Checking music state');
              // Always check sources in PWA on focus
              if (isPWA && globalMusicStarted && !checkSourcesValid()) {
                console.log('🎵 Focus: PWA sources invalid, recreating and restarting');
                globalMusicStarted = false;
                recreateSources().then(success => {
                  if (success) {
                    startMusic();
                  }
                });
              } else if (!globalMusicStarted) {
                console.log('🎵 Focus: Restarting music');
                startMusic();
              }
            }
          }).catch(error => {
            console.error('🎵 Focus: Failed to resume context:', error);
          });
        } else if (audioContextRef.current.state === 'running') {
          // Context is running - check music state
          if (globalTracksLoaded && soundEnabled) {
            // For PWA, always verify sources on focus
            if (isPWA && globalMusicStarted && !checkSourcesValid()) {
              console.log('🎵 Focus: PWA sources invalid while context running');
              globalMusicStarted = false;
              recreateSources().then(success => {
                if (success) {
                  startMusic();
                }
              });
            } else if (!globalMusicStarted) {
              console.log('🎵 Focus: Context running but music stopped, restarting');
              startMusic();
            }
          }
        }
      } else if (isPWA && globalIsInitialized) {
        // PWA might have lost context entirely
        console.log('🎵 Focus: PWA lost audio context, needs reinitialization');
        resetGlobalAudioState();
        setIsLoaded(false);
      }
    };
    
    // Handle blur events to track when we lose focus
    const handleBlur = () => {
      console.log('🎵 Window blurred');
      if (globalMusicStarted) {
        console.log('🎵 Music was playing when window lost focus');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);
  
  // Handle mute/unmute when soundEnabled changes
  useEffect(() => {
    if (masterGainRef.current) {
      // Try to resume context if suspended
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        console.log('🎵 Context suspended during sound change, attempting resume...');
        audioContextRef.current.resume().then(() => {
          console.log('🎵 Context resumed after sound change, state:', audioContextRef.current.state);
        }).catch(error => {
          console.error('🎵 Failed to resume context after sound change:', error);
        });
      }
      
      if (soundEnabled) {
        console.log('🎵 Sound enabled, fading in music to:', musicVolume * 0.75);
        // Fade in to current music volume (50% of slider)
        masterGainRef.current.gain.cancelScheduledValues(audioContextRef.current.currentTime);
        masterGainRef.current.gain.setValueAtTime(masterGainRef.current.gain.value, audioContextRef.current.currentTime);
        masterGainRef.current.gain.linearRampToValueAtTime(musicVolume * 0.75, audioContextRef.current.currentTime + 0.2);
      } else {
        console.log('🎵 Sound disabled, fading out music');
        // Fade out to 0 (mute)
        masterGainRef.current.gain.cancelScheduledValues(audioContextRef.current.currentTime);
        masterGainRef.current.gain.setValueAtTime(masterGainRef.current.gain.value, audioContextRef.current.currentTime);
        masterGainRef.current.gain.linearRampToValueAtTime(0, audioContextRef.current.currentTime + 0.2);
      }
    }
  }, [soundEnabled, musicVolume]);

  // Update master volume when musicVolume prop changes
  useEffect(() => {
    if (masterGainRef.current && soundEnabled) {
      masterGainRef.current.gain.value = musicVolume * 0.75;  // 75% of slider value
    }
  }, [musicVolume, soundEnabled]);

  // Handle music state when tracks first load (only run once when isLoaded becomes true)
  useEffect(() => {
    if (isLoaded && audioContextRef.current) {
      console.log('🎵 Tracks loaded, initializing music state for combo:', combo);
      const currentTime = audioContextRef.current.currentTime;
      
      if (combo < 2) {
        // Start with idle music
        console.log('🎵 Starting with idle music');
        setIsIdle(true);
        if (idleGainRef.current) {
          idleGainRef.current.gain.setValueAtTime(0.5, currentTime);
        }
        // Ensure gameplay tracks are silent
        layers.forEach(layer => {
          const gainNode = gainNodesRef.current[layer.name];
          if (gainNode) {
            gainNode.gain.setValueAtTime(0, currentTime);
          }
        });
      } else {
        // Start with gameplay music - immediately set correct volumes
        console.log('🎵 Starting with gameplay music for combo:', combo);
        setIsIdle(false);
        // Mute idle music immediately
        if (idleGainRef.current) {
          idleGainRef.current.gain.setValueAtTime(0, currentTime);
        }
        // Set up gameplay layers based on current combo
        layers.forEach(layer => {
          const gainNode = gainNodesRef.current[layer.name];
          if (gainNode) {
            const shouldBeActive = combo >= layer.threshold;
            const targetGain = shouldBeActive ? 0.8 : 0;
            console.log(`🎵 Initial state - ${layer.name}: ${shouldBeActive ? 'ON' : 'OFF'} (gain: ${targetGain})`);
            gainNode.gain.setValueAtTime(targetGain, currentTime);
          }
        });
        
        // Set playback rate if needed
        if (combo >= 300) {
          const speedSteps = Math.floor(combo / 100) - 2;
          const speedIncrease = speedSteps * 0.1;
          const playbackRate = 1 + speedIncrease;
          
          Object.values(tracksRef.current).forEach(track => {
            if (track && track.source && track.source.playbackRate) {
              track.source.playbackRate.setValueAtTime(playbackRate, currentTime);
            }
          });
        }
      }
    }
  }, [isLoaded]); // Only run when isLoaded changes (once when tracks load)

  useEffect(() => {
    console.log('Combo changed:', combo, 'isLoaded:', isLoaded, 'isIdle:', isIdle);
    
    if (!isLoaded) return;
    
    // Handle idle vs gameplay state based on combo
    if (combo < 2) {
      if (!isIdle) {
        // Transition to idle music (fast fade on debounce)
        console.log('Transitioning to idle music');
        setIsIdle(true);
        fadeToIdleMusic();
      }
    } else {
      if (isIdle) {
        // Transition to gameplay music (only when combo >= 2)
        console.log('Transitioning to gameplay music');
        setIsIdle(false);
        fadeToGameplayMusic();
      }
      updateLayers();
      updatePlaybackRate();
    }
  }, [combo, isLoaded]);

  const fadeToIdleMusic = () => {
    console.log('🎵 fadeToIdleMusic called');
    const currentTime = audioContextRef.current.currentTime;
    const gameplayFadeTime = 0.1; // Very fast fade out for gameplay music
    const idleFadeTime = 3.0; // Very gentle fade in for idle music (3 seconds)
    
    // Fade in idle music gently
    if (idleGainRef.current) {
      console.log('🎵 Fading in idle music from', idleGainRef.current.gain.value, 'to 0.5');
      idleGainRef.current.gain.cancelScheduledValues(currentTime);
      idleGainRef.current.gain.setValueAtTime(idleGainRef.current.gain.value, currentTime);
      idleGainRef.current.gain.linearRampToValueAtTime(0.5, currentTime + idleFadeTime);
    } else {
      console.log('🎵 No idle gain to fade in!');
    }
    
    // Fade out all gameplay layers very quickly
    layers.forEach(layer => {
      const gainNode = gainNodesRef.current[layer.name];
      if (!gainNode) return;
      
      gainNode.gain.cancelScheduledValues(currentTime);
      gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
      gainNode.gain.linearRampToValueAtTime(0, currentTime + gameplayFadeTime);
    });
    
    setCurrentLayers(0);
    
    // Reset playback rate with synchronized timing
    const resetTime = currentTime;
    Object.values(tracksRef.current).forEach(track => {
      if (track.source.playbackRate) {
        track.source.playbackRate.setValueAtTime(1, resetTime);
      }
    });
    
    // Also reset idle track playback rate
    if (idleTrackRef.current && idleTrackRef.current.source.playbackRate) {
      idleTrackRef.current.source.playbackRate.setValueAtTime(1, resetTime);
    }
  };

  const fadeToGameplayMusic = () => {
    console.log('Fading to gameplay music...');
    const currentTime = audioContextRef.current.currentTime;
    const fadeTime = 0.3;
    
    // Fade out idle music
    if (idleGainRef.current) {
      console.log('Fading out idle music');
      idleGainRef.current.gain.cancelScheduledValues(currentTime);
      idleGainRef.current.gain.setValueAtTime(idleGainRef.current.gain.value, currentTime);
      idleGainRef.current.gain.linearRampToValueAtTime(0, currentTime + fadeTime);
    }
    
    // Drums will fade in immediately when gameplay starts
    const drumsGain = gainNodesRef.current['drums'];
    if (drumsGain) {
      console.log('Fading in drums');
      drumsGain.gain.cancelScheduledValues(currentTime);
      drumsGain.gain.setValueAtTime(drumsGain.gain.value, currentTime);
      drumsGain.gain.linearRampToValueAtTime(0.8, currentTime + fadeTime);
    } else {
      console.log('No drums gain node found!');
    }
  };

  const updateLayers = () => {
    let activeLayers = 0;
    
    console.log('Updating layers for combo:', combo);
    
    layers.forEach(layer => {
      const gainNode = gainNodesRef.current[layer.name];
      if (!gainNode) {
        console.log('No gain node for:', layer.name);
        return;
      }
      
      const shouldBeActive = combo >= layer.threshold;
      const currentGain = gainNode.gain.value;
      const targetGain = shouldBeActive ? 0.8 : 0;
      
      console.log(`Layer ${layer.name}: shouldBeActive=${shouldBeActive}, currentGain=${currentGain}, targetGain=${targetGain}`);
      
      // Only update if there's a significant difference (tolerance for floating point)
      const tolerance = 0.01;
      if (Math.abs(currentGain - targetGain) > tolerance) {
        gainNode.gain.cancelScheduledValues(audioContextRef.current.currentTime);
        gainNode.gain.setValueAtTime(currentGain, audioContextRef.current.currentTime);
        gainNode.gain.linearRampToValueAtTime(
          targetGain,
          audioContextRef.current.currentTime + 0.3
        );
      }
      
      if (shouldBeActive) activeLayers++;
    });
    
    setCurrentLayers(activeLayers);
  };

  const updatePlaybackRate = () => {
    // Speed increases start after all layers are active (300+ combo)
    if (combo < 300) return;
    
    // 10% speed increase per 100 clicks starting at 300
    // At combo 300-399: 1.1x speed  
    // At combo 400-499: 1.2x speed
    // At combo 500-599: 1.3x speed
    // At combo 600-699: 1.4x speed
    // No cap - keeps increasing forever!
    const speedSteps = Math.floor(combo / 100) - 2;  // -2 because we start at 300 (third hundred)
    const speedIncrease = speedSteps * 0.1;
    const playbackRate = 1 + speedIncrease;
    
    // Update all track playback rates at the same scheduled time to maintain sync
    const updateTime = audioContextRef.current.currentTime;
    
    Object.values(tracksRef.current).forEach(track => {
      if (track.source.playbackRate) {
        track.source.playbackRate.setValueAtTime(playbackRate, updateTime);
      }
    });
    
    // Also update idle track to keep all audio in sync
    if (idleTrackRef.current && idleTrackRef.current.source.playbackRate) {
      idleTrackRef.current.source.playbackRate.setValueAtTime(playbackRate, updateTime);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      left: '10px',
      color: '#666',
      fontSize: '12px',
      fontFamily: 'monospace',
      opacity: 0.7,
      zIndex: 3
    }}>
      {isLoaded && (
        <div>
          {isIdle ? (
            <span>♪ Idle Music</span>
          ) : (
            <>
              ♪ Layers: {currentLayers}/3
              {combo >= 300 && ` • Speed: ${Math.round((1 + (Math.floor(combo / 100) - 2) * 0.1) * 100)}%`}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MusicManager;
